// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-expect-error
import { EveryMessage } from "@sendbird/uikit-react/types/types";

import { ChatMessageItem } from "@/components/shared/chat-message-item";
import type { ChatMessage } from "@/components/shared/chat-message-types";
import { useResolveFileMessage } from "@/lib/api/messages";
import { useUser } from "@/lib/api/user";
import { normalizeConversationMembers } from "@/lib/sendbird/conversation-members";
import { getSendbirdMessageSenderDisplayName } from "@/lib/redacted-display";
import { useSignedMedia } from "@/lib/hooks/use-signed-media";
import { isMessageHidden } from "@/lib/utils/parse-message-data";
import { useChannelContext } from "@sendbird/uikit-react/Channel/context";
import EmojiReactions from "@sendbird/uikit-react/ui/EmojiReactions";
import useSendbirdStateContext from "@sendbird/uikit-react/useSendbirdStateContext";
import { ReplyIcon } from "lucide-react";
import { memo, useCallback, useMemo } from "react";

interface MessageItemProps {
  message: EveryMessage;
}

interface MessageItemViewProps {
  message: EveryMessage;
  isRead: boolean;
  /** uikit mutates reactions in place, so message identity can't detect them. */
  reactionsKey: string;
  store: ReturnType<typeof useSendbirdStateContext>;
  channelStore: ReturnType<typeof useChannelContext>;
}

const getReactionsKey = (message: EveryMessage): string =>
  JSON.stringify(
    (message.reactions ?? []).map((r: { key: string; userIds: string[] }) => [
      r.key,
      r.userIds,
    ]),
  );

// Everything else (user id, channel url, emoji set) only changes on remount.
export const areMessageItemPropsEqual = (
  prev: MessageItemViewProps,
  next: MessageItemViewProps,
) =>
  prev.message === next.message &&
  prev.reactionsKey === next.reactionsKey &&
  prev.isRead === next.isRead;

/**
 * The legacy uikit Channel module re-renders every row on every channel event
 * (message, read receipt, typing). Reading the contexts here and rendering a
 * memoized view keeps unchanged rows from re-running their queries.
 */
export function MessageItem({ message }: MessageItemProps) {
  const store = useSendbirdStateContext();
  const channelStore = useChannelContext();
  const isRead =
    message.sendingStatus === "succeeded" &&
    channelStore.currentGroupChannel?.getUnreadMemberCount?.(message) === 0;

  return (
    <MessageItemView
      message={message}
      isRead={isRead}
      reactionsKey={getReactionsKey(message)}
      store={store}
      channelStore={channelStore}
    />
  );
}

const MessageItemView = memo(function MessageItemView({
  message,
  isRead,
  store,
  channelStore,
}: MessageItemViewProps) {
  const userId = store.config.userId;
  const channel = channelStore.currentGroupChannel;
  const isCurrentUser = message?.sender?.userId === userId;

  const senderDisplayName = useMemo(
    () => getSendbirdSenderDisplayName(message),
    [message],
  );

  const { data: senderUser } = useUser(message?.sender?.userId ?? "");
  const { url: profileUrl } = useSignedMedia(
    senderUser?.profile_picture_url ?? undefined,
  );
  const conversationMembers = normalizeConversationMembers(channel?.members);

  const fileMessage = message.isFileMessage() ? message : null;
  const isAudioMessage = fileMessage?.type.includes("audio") ?? false;
  const { transcription, isFetching, requestTranscript } =
    useResolveFileMessage(
      isAudioMessage ? String(message.messageId) : null,
      message.reqId ?? null,
      { eager: false },
    );

  const chatMessage = useMemo(
    () =>
      normalizeSendbirdMessage(message, senderDisplayName, {
        transcription,
        transcriptionPending: isFetching,
      }),
    [message, senderDisplayName, transcription, isFetching],
  );

  const isHidden = isMessageHidden((message as { data?: string }).data);

  const handleQuoteMessage = useCallback(() => {
    channelStore.setQuoteMessage?.(message);
  }, [channelStore, message]);

  return (
    <ChatMessageItem
      message={chatMessage}
      layout={{
        alignment: isCurrentUser ? "right" : "left",
        messageStyle: isCurrentUser ? "outgoing" : "incoming",
      }}
      sender={{
        label: isCurrentUser ? "You" : senderDisplayName,
        profileUrl: profileUrl,
      }}
      slots={{
        bubbleAccessories:
          !message.parentMessage && !isHidden ? (
            <button
              onClick={handleQuoteMessage}
              className="flex min-h-10 min-w-10 cursor-pointer items-center justify-center rounded-full hover:bg-gray-100 active:bg-gray-200"
            >
              <ReplyIcon />
            </button>
          ) : null,
        metaExtra:
          isCurrentUser && isRead ? (
            <>
              <div className="h-1 w-1 rounded-full bg-gray-600" />
              <span className="text-xs uppercase text-gray-500">Read</span>
            </>
          ) : null,
        reactions: message.sender ? (
          <EmojiReactions
            userId={userId}
            message={message}
            channel={channel}
            emojiContainer={store.emojiManager.emojiContainer}
            memberNicknamesMap={channelStore.nicknamesMap}
            toggleReaction={channelStore.toggleReaction}
          />
        ) : null,
      }}
      context={{
        surface: "mentor",
        conversation: {
          id: channel?.url ?? null,
          members: conversationMembers,
        },
      }}
      behavior={{
        onRequestTranscript: requestTranscript,
        onParentMessageClick: (parentMessage) => {
          document
            .querySelector(`[data-sb-message-id="${parentMessage.id}"]`)
            ?.scrollIntoView({ behavior: "smooth" });
        },
      }}
    />
  );
}, areMessageItemPropsEqual);

function getSendbirdSenderDisplayName(message: EveryMessage): string {
  const senderMetaRole = (
    message?.sender as { metaData?: { role?: string } } | undefined
  )?.metaData?.role;

  if (!message?.sender) {
    return "";
  }

  return (
    getSendbirdMessageSenderDisplayName({
      user_id: message.sender.userId,
      nickname: message.sender.nickname,
      metadata: { role: senderMetaRole },
    }) ?? message.sender.nickname
  );
}

function normalizeSendbirdMessage(
  message: EveryMessage,
  senderDisplayName: string,
  transcript?: {
    transcription?: string | null;
    transcriptionPending?: boolean;
  },
): ChatMessage {
  return {
    id: message.messageId,
    kind: message.isFileMessage()
      ? "file"
      : message.isAdminMessage()
        ? "admin"
        : "message",
    text: message.message,
    data: (message as { data?: string }).data,
    customType: message.customType,
    createdAt: message.createdAt,
    sender: message.sender
      ? {
          id: message.sender.userId,
          nickname: message.sender.nickname,
          displayName: senderDisplayName,
          profileUrl: message.sender.profileUrl,
        }
      : null,
    attachment: message.isFileMessage()
      ? {
          url: (message as { url?: string }).url,
          mimeType: (message as { type?: string }).type,
        }
      : null,
    transcription: transcript?.transcription ?? null,
    transcriptionPending: transcript?.transcriptionPending,
    parentMessage: message.parentMessage
      ? normalizeSendbirdMessage(
          message.parentMessage as EveryMessage,
          getSendbirdSenderDisplayName(message.parentMessage as EveryMessage),
        )
      : null,
    translations: (message as { translations?: Record<string, string> })
      .translations,
  };
}
